import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { JwtService } from '../services/jwt.service';
import { map, catchError, of, tap } from 'rxjs';
import { EmbedService } from '../services/embed.service';
import { logDebug, logWarn, logError } from '../core/services/logger.service';

/**
 * Vérifie si le token est présent et non expiré localement
 * @param jwtService - Service JWT
 * @returns boolean - true si le token est valide localement
 */
function isTokenValidLocally(jwtService: JwtService, embedService: EmbedService): boolean {
  const token = jwtService.getToken();
  if (!token) {
    logDebug('No token found in session storage');
    return false;
  }

  const decodedToken = jwtService.decodeToken();
  if (!decodedToken) {
    logDebug('Token could not be decoded');
    return false;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const isExpired = decodedToken.exp < currentTime;
  
  if (isExpired) {
    logDebug('Token is expired locally', {
      exp: decodedToken.exp,
      current: currentTime,
      expired: isExpired
    });
    return false;
  }
  checkEnableEmbed(embedService);
  logDebug('Token is valid locally');
  return true;
}

function checkEnableEmbed(embedService:EmbedService): void {
  const embedIsSet = embedService.getEmbedFromUrl();
  if (embedIsSet) {
    embedService.setEmbed(true);
  }
}

export const authGuard: CanActivateFn = (route, state) => {
  const jwtService = inject(JwtService);
  const embedService = inject(EmbedService);

  // Vérifier d'abord si le token est valide localement
  if (!isTokenValidLocally(jwtService, embedService)) {
    logDebug('AuthGuard: Token not valid locally, checking with API');
    // Si le token n'est pas valide localement, faire appel à l'API
    return jwtService.checkTokenAndRedirect().pipe(
      tap(isValid => logDebug('AuthGuard received verification result from API:', isValid)),
      map(isValid => {
        if (!isValid) {
          logDebug('AuthGuard: Token invalid, redirecting to login');
          // Redirection immédiate sans setTimeout
          jwtService.redirectLoginPage();
          return false;
        }

        logDebug('AuthGuard: Token valid, allowing access');
        checkEnableEmbed(embedService);
        return true;
      }),
      catchError((error) => {
        logError('AuthGuard caught error during token verification:', error);
        logDebug('AuthGuard: Error occurred, removing token and redirecting');
        jwtService.removeToken();
        // Redirection immédiate sans setTimeout
        jwtService.redirectLoginPage();
        return of(false);
      })
    );
  }

  logDebug('AuthGuard: Token valid locally, allowing access');
  // Si le token est valide localement, on peut directement retourner true
  return of(true);
};
